import { DatePicker, Form, Input, message, Modal, Space } from 'antd';
import React, { useEffect } from 'react';
import { useUserLevel } from 'reducers';
import { FullLooseOrder, PriceType, UserLevel } from 'types';

interface ReturnData {
	date: moment.Moment;
	time: moment.Moment | undefined;
	itemsToClaim: number;
	price: number;
}

interface Props {
	order: FullLooseOrder;
	visible: boolean;
	onCancel: () => void;
	onOk: (data: ReturnData) => Promise<void>;
}

export const ClaimingModal: React.FC<Props> = ({ order, visible, onCancel, onOk }) => {
	const userLevel = useUserLevel();
	const [date, setDate] = React.useState<moment.Moment>();
	const [time, setTime] = React.useState<moment.Moment>();
	const [itemsToClaim, setItemsToClaim] = React.useState<number>();
	const [price, setPrice] = React.useState<number>();
	const [loading, setLoading] = React.useState(false);

	useEffect(() => {
		setItemsToClaim(order.count);
	}, [order.count]);

	useEffect(() => {
		setPrice(order.priceType === PriceType.gross ? order.grossPrice : order.netPrice);
	}, [order.priceType, order.grossPrice, order.netPrice]);

	const accept = async () => {
		if (loading) {
			return;
		}

		if (!date) {
			message.error('Wybierz datę odbioru');
			return;
		}

		if (!itemsToClaim) {
			message.error('Podaj ilość sztuk do odebrania');
			return;
		}

		if (itemsToClaim < 1 || itemsToClaim > order.count) {
			message.error('Ilość sztuk do odebrania jest nieprawidłowa');
			return;
		}

		if (!price || price < 1) {
			message.error('Podaj prawidłową cenę');
			return;
		}

		try {
			setLoading(true);
			await onOk({ date, time, itemsToClaim, price });
		} catch (error) {
			console.error(error);
			message.error(`Wystąpił błąd (${error})`);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title=""
			open={visible}
			onCancel={onCancel}
			onOk={accept}
			cancelText="Anuluj"
			okText="Zatwierdź"
			cancelButtonProps={{ size: 'large' }}
			okButtonProps={{
				size: 'large',
				type: 'primary',
				loading,
			}}
		>
			<h1>Dodaj odbiór</h1>
			<Form layout="vertical">
				<Form.Item label="Termin odbioru">
					<Space size={20}>
						<DatePicker
							size="large"
							placeholder="Data"
							value={date ?? null}
							onChange={(v) => v && setDate(v)}
							format="YYYY-MM-DD"
						/>
						<DatePicker.TimePicker
							size="large"
							format="HH:mm"
							placeholder="Godzina"
							value={time ?? null}
							onChange={(v) => setTime(v ?? undefined)}
						/>
					</Space>
				</Form.Item>
				<Form.Item label="Ilość sztuk do odebrania">
					<Space size={10} direction="vertical" className="w-full">
						<Input
							size="large"
							type="number"
							value={itemsToClaim}
							onChange={(e) => setItemsToClaim(e.target.value ? parseInt(e.target.value) : undefined)}
							max={order?.count}
							min={1}
						/>
						{itemsToClaim !== undefined && order?.count !== undefined && itemsToClaim > order.count && (
							<span className="text-red-500">Ilość nie może być większa niż {order.count}</span>
						)}
						{itemsToClaim !== undefined && order?.count !== undefined && itemsToClaim < 1 && (
							<span className="text-red-500">Ilość nie może być mniejsza niż 1</span>
						)}
					</Space>
				</Form.Item>
				{userLevel && userLevel >= UserLevel.superAdmin && (
					<Form.Item label={`Cena (${order.priceType === PriceType.gross ? 'brutto' : 'netto'})`}>
						<Input
							size="large"
							type="number"
							suffix="PLN"
							value={price}
							onChange={(e) => setPrice(e.target.value ? parseFloat(e.target.value) : undefined)}
							min={1}
						/>
					</Form.Item>
				)}
			</Form>
		</Modal>
	);
};
